<template>
  <v-container>
    <h2 class="display-2 mb-4">
      <v-icon large color="primary" class="mr-2">person</v-icon>
      {{ $t("App.UserProfile") }}
    </h2>

    <v-card class="mb-4">
      <v-card-title class="headline white--text info">
        <v-icon class="mr-2" color="white">account_box</v-icon>
        Account Information
      </v-card-title>
      <v-card-text class="pa-0">
        <v-list two-line>
          <v-list-tile>
            <v-list-tile-avatar>
              <v-icon color="info">person</v-icon>
            </v-list-tile-avatar>
            <v-list-tile-content>
              <v-list-tile-title>Username</v-list-tile-title>
              <v-list-tile-sub-title class="font-weight-medium">{{ profile.UserName || '—' }}</v-list-tile-sub-title>
            </v-list-tile-content>
          </v-list-tile>
          <v-divider inset></v-divider>
          <v-list-tile>
            <v-list-tile-avatar>
              <v-icon color="info">email</v-icon>
            </v-list-tile-avatar>
            <v-list-tile-content>
              <v-list-tile-title>Email</v-list-tile-title>
              <v-list-tile-sub-title class="font-weight-medium">{{ profile.Email || '—' }}</v-list-tile-sub-title>
            </v-list-tile-content>
          </v-list-tile>
          <v-divider inset></v-divider>
          <v-list-tile>
            <v-list-tile-avatar>
              <v-icon color="info">business</v-icon>
            </v-list-tile-avatar>
            <v-list-tile-content>
              <v-list-tile-title>Tenant</v-list-tile-title>
              <v-list-tile-sub-title class="font-weight-medium">{{ profile.TenantName || '—' }}</v-list-tile-sub-title>
            </v-list-tile-content>
          </v-list-tile>
          <v-divider inset></v-divider>
          <v-list-tile>
            <v-list-tile-avatar>
              <v-icon color="info">security</v-icon>
            </v-list-tile-avatar>
            <v-list-tile-content>
              <v-list-tile-title>Role(s)</v-list-tile-title>
              <v-list-tile-sub-title class="font-weight-medium">{{ rolesDisplay || '—' }}</v-list-tile-sub-title>
            </v-list-tile-content>
          </v-list-tile>
        </v-list>
      </v-card-text>
    </v-card>

    <v-card class="mb-4">
      <v-card-title class="headline white--text secondary">
        <v-icon class="mr-2" color="white">vpn_key</v-icon>
        Access Token
      </v-card-title>
      <v-card-text>
        <v-layout row align-center>
          <v-flex grow>
            <div class="token-display">{{ maskedToken }}</div>
          </v-flex>
          <v-flex shrink>
            <v-btn
              icon
              :color="copied ? 'success' : 'accent'"
              @click="copyToken"
            >
              <v-icon>{{ copied ? 'check' : 'mdi-content-copy' }}</v-icon>
            </v-btn>
          </v-flex>
        </v-layout>
        <div class="caption mt-2">
          <v-icon small class="mr-1">schedule</v-icon>
          Expires: {{ tokenExpiry }}
        </div>
      </v-card-text>
    </v-card>

    <v-card class="mb-4">
      <v-card-title class="headline white--text error">
        <v-icon class="mr-2" color="white">lock</v-icon>
        Change Password
      </v-card-title>
      <v-divider></v-divider>
      <v-form ref="passwordFormRef" @submit.prevent="changePassword" lazy-validation>
        <v-card-text>
          <v-alert :value="true" color="warning" icon="warning" outline>
            Changing your password will log you out of all active sessions. You will need to sign in again with your new password.
          </v-alert>
          <v-text-field
            v-model="passwordForm.CurrentPassword"
            label="Current Password"
            type="password"
            box
            prepend-icon="lock_open"
            :rules="[rules.required]"
          ></v-text-field>
          <v-text-field
            v-model="passwordForm.NewPassword"
            label="New Password"
            type="password"
            box
            prepend-icon="lock"
            :rules="newPasswordRules"
            hint="Minimum 6 characters"
            persistent-hint
          ></v-text-field>
          <v-text-field
            v-model="passwordForm.ConfirmPassword"
            label="Confirm New Password"
            type="password"
            box
            prepend-icon="lock"
            :rules="confirmPasswordRules"
          ></v-text-field>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-3">
          <v-spacer></v-spacer>
          <v-btn
            color="error"
            type="submit"
            :loading="isSubmitting"
            :disabled="isSubmitting"
            large
          >
            <v-icon left>save</v-icon>
            Change Password
          </v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-container>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'

const { mapGetters, mapActions } = createNamespacedHelpers('authenticate')
const app = createNamespacedHelpers('app')

export default {
  name: 'UserProfile',
  data() {
    return {
      profile: {
        UserName: '',
        Email: '',
        TenantName: '',
        Roles: [],
      },
      passwordForm: {
        CurrentPassword: '',
        NewPassword: '',
        ConfirmPassword: '',
      },
      isSubmitting: false,
      copied: false,
      rules: {
        required: value => !!value || 'Required.',
        minLength: value => (value && value.length >= 6) || 'Minimum 6 characters.',
      },
    }
  },
  computed: {
    ...mapGetters(['accessInfo']),
    rolesDisplay() {
      return this.profile.Roles ? this.profile.Roles.join(', ') : ''
    },
    maskedToken() {
      const token = this.accessInfo.token || ''
      if (token.length <= 14) return token
      return token.substring(0, 10) + '...' + token.substring(token.length - 4)
    },
    tokenExpiry() {
      if (this.accessInfo.expiredTime) {
        return this.accessInfo.expiredTime.toLocaleString()
      }
      return 'N/A'
    },
    newPasswordRules() {
      return [this.rules.required, this.rules.minLength]
    },
    confirmPasswordRules() {
      return [
        this.rules.required,
        value => value === this.passwordForm.NewPassword || 'Passwords do not match.',
      ]
    },
  },
  methods: {
    ...mapActions(['logOut']),
    ...app.mapActions(['setSnackbar']),
    async fetchProfile() {
      try {
        const res = await this.axios.get('api/Account/Me')
        this.profile = res.data
      } catch (error) {
        this.setSnackbar({
          text: 'Failed to load profile information.',
          color: 'error',
          icon: 'error',
        })
      }
    },
    async changePassword() {
      if (!this.$refs.passwordFormRef.validate()) return

      this.isSubmitting = true
      try {
        await this.axios.post('api/Account/ChangePassword', this.passwordForm)
        this.setSnackbar({
          text: 'Password updated successfully. Please log in with your new credentials.',
          color: 'success',
          icon: 'check',
        })
        await this.logOut({ redirect: false })
        this.$router.push({ name: 'login', query: { message: 'Password updated successfully. Please log in with your new credentials.' } })
      } catch (error) {
        let message = 'Password change failed. Please check your inputs and try again.'
        if (error.response && error.response.data) {
          if (error.response.data.Message) {
            message = error.response.data.Message
          } else if (error.response.data.ModelState) {
            const errors = Object.values(error.response.data.ModelState).flat()
            if (errors.length) message = errors.join(' ')
          }
        }
        this.setSnackbar({
          text: message,
          color: 'error',
          icon: 'error',
        })
      } finally {
        this.isSubmitting = false
      }
    },
    async copyToken() {
      try {
        await navigator.clipboard.writeText(this.accessInfo.token)
        this.copied = true
        setTimeout(() => { this.copied = false }, 2000)
      } catch (err) {
        console.error('Copy failed:', err)
      }
    },
  },
  mounted() {
    this.fetchProfile()
  },
}
</script>

<style scoped>
.token-display {
  font-family: 'Courier New', Courier, monospace;
  font-size: 14px;
  background-color: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 10px 14px;
  word-break: break-all;
  color: #333;
}
</style>
